// InteliChat adapter: the Nextlevel BFF (Rails backend-for-frontend).
// Shared bffGet/bffPost helpers for ALL InteliChat workspaces - the same
// Option-B security pattern as MvpCustomerIntel.vue: same-origin,
// session-authed via window.axios; the browser holds NO Magento secret.
// Route: /api/v1/accounts/:accountId/nextlevel/agent/{intel|write}
// (Rails resolves customer/order identity server-side from the
// conversation and signs the s2s hop to Magento itself.)
//
// Every call is explicitly conversation-scoped (the Rails controller
// 404s otherwise) - callers pass the conversation display_id.

const accountIdFromContext = explicitAccountId => {
  if (explicitAccountId) return String(explicitAccountId);
  const match = window.location.pathname.match(/\/accounts\/(\d+)/) || [];
  return String(match[1] || '');
};

export function useInteliBff(accountIdRef = null) {
  const resolveAccountId = () =>
    accountIdFromContext(accountIdRef?.value ?? accountIdRef);

  // BFF read: proxy_action in the Rails READ_ACTIONS allowlist
  // (intel / escalations / order / triage / orderlookup).
  const bffGet = async (action, conversationId, extra = {}) => {
    const aid = resolveAccountId();
    if (!aid || !conversationId)
      throw new Error('bff: no account/conversation');
    const resp = await window.axios.get(
      `/api/v1/accounts/${aid}/nextlevel/agent/intel`,
      {
        params: {
          proxy_action: action,
          conversation_id: String(conversationId),
          ...extra,
        },
      }
    );
    return resp.data;
  };

  // BFF ACCOUNT-level read: proxy_action in READ_ACTIONS but NOT
  // conversation-scoped (e.g. 'revenue' -> nlh_chat_revenue_daily
  // aggregate). Same session-authed same-origin route; the Rails
  // controller handles these before the conversation lookup.
  const bffGetAccount = async (action, extra = {}) => {
    const aid = resolveAccountId();
    if (!aid) throw new Error('bff: no account');
    const resp = await window.axios.get(
      `/api/v1/accounts/${aid}/nextlevel/agent/intel`,
      { params: { proxy_action: action, ...extra } }
    );
    return resp.data;
  };

  // BFF write: proxy_action in the Rails WRITE_ACTIONS allowlist.
  const bffPost = async (action, conversationId, params = {}) => {
    const aid = resolveAccountId();
    if (!aid || !conversationId)
      throw new Error('bff: no account/conversation');
    const { data } = await window.axios.post(
      `/api/v1/accounts/${aid}/nextlevel/agent/write`,
      {
        proxy_action: action,
        conversation_id: String(conversationId),
        ...params,
      }
    );
    return data;
  };

  return { bffGet, bffGetAccount, bffPost };
}
